/**
 * =========================================================================================
 *   Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *   Module       : components/errors/ErrorDetailDrawer
 *   Feature      : 252 - Backup Error Visibility & Guided Remediation
 *   Description  : The error DETAIL an operator could never see before (FR-002): the full raw
 *                  message, the attempt history, the specific failing items (FR-003), and the
 *                  remediation inline in the SAME place (FR-005).
 *
 *                  Mounted globally (see GlobalBackupErrorDrawer) so an ALERT can open it from
 *                  anywhere — the bell, /alerts, or the Errors screen (FR-008).
 *   Author       : Peter Dobler (Dobler Data Solutions)
 * =========================================================================================
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, DescriptionList, Badge, Button, LoadingState, ErrorState } from '@/components/ui';
import { formatEastern } from '@/utils/dateFormatter';
import backupErrorsApi, { BackupErrorDetail, RemediationStep } from '../../api/backupErrors';
import { CauseClassBadge, StatusBadge } from './CauseClassBadge';
import RemediationPanel from './RemediationPanel';

interface Props {
  errorKey: string | null;
  /** Pin the display to the run the operator clicked. Null = the problem's latest occurrence. */
  runId?: string | null;
  onClose: () => void;
  /** false when the current role may not record attempts / escalate (FR-017). */
  canAct?: boolean;
}

export const ErrorDetailDrawer: React.FC<Props> = ({ errorKey, runId = null, onClose, canAct = true }) => {
  const [data, setData] = useState<BackupErrorDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [escalateWarning, setEscalateWarning] = useState<number[] | null>(null);

  const load = useCallback(async () => {
    if (!errorKey) return;
    setLoading(true); setErr(null);
    try {
      setData(await backupErrorsApi.detail(errorKey, runId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load the error detail');
    } finally {
      setLoading(false);
    }
  }, [errorKey, runId]);

  useEffect(() => { void load(); }, [load]);

  const record = async (step: RemediationStep, outcome: 'attempted' | 'succeeded' | 'failed' | 'waived', note?: string) => {
    if (!errorKey || !data) return;
    setBusy(true);
    try {
      await backupErrorsApi.recordAttempt(errorKey, {
        // the code the STEPS came from, not the placeholder — otherwise the trail records an
        // attempt against 'unknown' while the operator was following container_failed's steps
        catalogCode: data.resolvedCode, stepIndex: step.n, outcome, note,
      });
      await load();
    } finally { setBusy(false); }
  };

  /** FR-012 — warn while client-actionable steps remain un-attempted, then allow acknowledgement. */
  const escalate = async (acknowledge = false) => {
    if (!errorKey) return;
    setBusy(true);
    try {
      await backupErrorsApi.escalate(errorKey, { acknowledgeUnattempted: acknowledge });
      setEscalateWarning(null);
      await load();
    } catch (e) {
      const unattempted = (e as { response?: { data?: { error?: { unattemptedSteps?: number[] } } } })
        ?.response?.data?.error?.unattemptedSteps;
      if (unattempted?.length) setEscalateWarning(unattempted);
      else setErr(e instanceof Error ? e.message : 'Escalation failed');
    } finally { setBusy(false); }
  };

  return (
    <Drawer isOpen={!!errorKey} onClose={onClose} title="Backup error detail" width="xl">
      {loading && <LoadingState />}
      {!loading && err && <ErrorState message={err} onRetry={() => void load()} />}
      {!loading && !err && data && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={data.status} />
            <CauseClassBadge causeClass={data.causeClass} />
            <Badge variant="default">{data.severity}</Badge>
            <Badge variant="default" title="The code this remediation comes from">{data.resolvedCode}</Badge>
            {data.resolvedCode !== data.storedCode && (
              // Say both, rather than silently showing one. The surface stores no failure category,
              // so the database literally holds 'unknown'; the code above was read from the message.
              <Badge variant="default" title="What the database actually holds for this run">
                recorded as {data.storedCode}
              </Badge>
            )}
          </div>

          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{data.title}</h2>

          <DescriptionList
            columns={2}
            items={[
              { label: 'Entity', value: data.entityName ?? '—' },
              { label: 'Type', value: data.entityType ?? '—' },
              { label: 'Surface', value: data.surface },
              { label: 'Run id', value: data.runId ?? '—' },
              { label: 'Occurred', value: data.occurredAt ? formatEastern(data.occurredAt) : '—' },
              { label: 'Consecutive occurrences', value: String(data.attemptCount ?? 0) },
              { label: 'First seen', value: data.firstSeen ? formatEastern(data.firstSeen) : '—' },
              { label: 'Last seen', value: data.lastSeen ? formatEastern(data.lastSeen) : '—' },
            ]}
          />

          {/* FR-002 — the full raw text, scrollable so a multi-KB LRO body never breaks the layout */}
          <section>
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Raw error</h3>
            {data.rawDetail ? (
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3 text-xs text-[var(--color-text-secondary)]">
                {data.rawDetail}
              </pre>
            ) : (
              <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
                This surface records no error text — the classification above is the only detail stored.
              </p>
            )}
          </section>

          {/* FR-003 — the SPECIFIC items that failed, not just a workspace-level message */}
          {data.affectedObjects.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                Affected items ({data.affectedObjects.length})
              </h3>
              <ul className="mt-2 space-y-2">
                {data.affectedObjects.map((o, i) => (
                  <li key={`${o.name}-${i}`} className="rounded-md border border-[var(--color-border)] p-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[var(--color-text-primary)]">{o.name}</span>
                      <Badge variant="default">{o.type}</Badge>
                      <Badge variant={o.status === 'Unsupported' ? 'info' : 'danger'}>{o.status}</Badge>
                      {o.occurrences > 1 && (
                        // the item is listed ONCE; how often it recurred is a count, not more rows
                        <span className="text-xs text-[var(--color-text-secondary)]">
                          on {o.occurrences} runs
                        </span>
                      )}
                    </div>
                    {o.detail && (
                      <p className="mt-1 break-words text-xs text-[var(--color-text-secondary)]">{o.detail}</p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <RemediationPanel
            remediation={data.remediation}
            isAcceptedLimitation={data.isAcceptedLimitation}
            onRecord={canAct ? record : undefined}
            busy={busy}
          />

          {/* FR-012 — escalation carries the trail and warns first */}
          {canAct && !data.isAcceptedLimitation && (
            <section className="border-t border-[var(--color-border)] pt-4">
              {escalateWarning && (
                <div className="mb-2 rounded-md border border-[var(--color-warning)] bg-[var(--color-bg-subtle)] p-3">
                  <p className="text-sm text-[var(--color-text-primary)]">
                    Step{escalateWarning.length > 1 ? 's' : ''} {escalateWarning.join(', ')} {escalateWarning.length > 1 ? 'have' : 'has'} not
                    been attempted or waived. Escalating now records that the self-service path was not exhausted.
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => void escalate(true)}>
                      Escalate anyway
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEscalateWarning(null)}>Keep working</Button>
                  </div>
                </div>
              )}
              {data.escalated ? (
                <Badge variant="info">Escalated — the remediation trail is attached</Badge>
              ) : (
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => void escalate(false)}>
                  Escalate to the platform team
                </Button>
              )}
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default ErrorDetailDrawer;
